import { useState, useEffect, useRef, useCallback } from 'react';

interface UseDropdownKeyboardNavigationOptions {
  isOpen: boolean;
  onSubMenuClose?: () => void;
  /**
   * 아이템들이 변경되었을 때 포커스를 리셋하기 위한 의존성.
   */
  itemCount?: number;
  disableItemFocus?: boolean;
  /**
   * itemCount 기반으로 키보드 네비게이션 수행 (가상화 리스트 대응)
   */
  useItemCountForNavigation?: boolean;
  /**
   * 인덱스 이동 시 해당 아이템을 렌더링하도록 스크롤 요청
   */
  onNavigateToIndex?: (index: number) => void;
  /**
   * itemCount 기반 네비게이션 시 item key 목록 (검색/필터링으로 인덱스 변경 대응)
   */
  itemKeys?: string[];
}

interface UseDropdownKeyboardNavigationReturn {
  menuRef: React.RefObject<HTMLDivElement>;
  handleKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  handleMouseOver: (e: React.MouseEvent<HTMLDivElement>) => void;
}

export const useDropdownKeyboardNavigation = ({
  isOpen,
  onSubMenuClose,
  itemCount,
  disableItemFocus = false,
  useItemCountForNavigation = false,
  onNavigateToIndex,
  itemKeys,
}: UseDropdownKeyboardNavigationOptions): UseDropdownKeyboardNavigationReturn => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const focusedKeyRef = useRef<string | null>(null);

  // 포커스 가능한 아이템들을 가져오는 함수
  const getFocusableItems = useCallback((): HTMLElement[] => {
    if (!menuRef.current) return [];
    return Array.from(
      menuRef.current.querySelectorAll<HTMLElement>(
        '[role="menuitem"]:not([aria-disabled="true"])',
      ),
    );
  }, []);

  // data-focused 속성 업데이트
  const updateFocusedAttribute = useCallback(
    (index: number) => {
      const focusableItems = getFocusableItems();
      focusableItems.forEach((item, i) => {
        const dataIndex = item.dataset.index;
        const isMatch = useItemCountForNavigation
          ? (dataIndex ? Number(dataIndex) : i) === index
          : i === index;
        if (isMatch) {
          item.setAttribute('data-focused', 'true');
          return;
        }

        item.removeAttribute('data-focused');
      });
    },
    [getFocusableItems, useItemCountForNavigation],
  );

  // 드롭다운이 열릴 때 또는 items 개수가 변경될 때 첫 번째 항목에 포커스
  useEffect(() => {
    if (!isOpen) {
      setFocusedIndex(-1);
      focusedKeyRef.current = null;
      return;
    }

    const hasItems = itemCount == null ? true : itemCount > 0;
    if (!hasItems) {
      setFocusedIndex(-1);
      focusedKeyRef.current = null;
      return;
    }

    // DOM이 렌더링된 후 포커스 설정
    requestAnimationFrame(() => {
      if (useItemCountForNavigation) {
        if (itemKeys && itemKeys.length > 0) {
          const currentKey = focusedKeyRef.current;
          if (currentKey) {
            const keyIndex = itemKeys.indexOf(currentKey);
            if (keyIndex !== -1) {
              setFocusedIndex(keyIndex);
              return;
            }
          }
          focusedKeyRef.current = itemKeys[0] ?? null;
        }
        setFocusedIndex(0);
        return;
      }

      const focusableItems = getFocusableItems();
      if (focusableItems.length > 0) {
        setFocusedIndex(0);
      }
    });
  }, [getFocusableItems, isOpen, itemCount, itemKeys, useItemCountForNavigation]);

  useEffect(() => {
    if (!isOpen || !useItemCountForNavigation || !itemKeys) return;

    if (itemKeys.length === 0) {
      if (focusedIndex !== -1) {
        setFocusedIndex(-1);
      }
      focusedKeyRef.current = null;
      return;
    }

    const currentKey = focusedKeyRef.current;
    if (!currentKey) {
      focusedKeyRef.current = itemKeys[0];
      if (focusedIndex !== 0) {
        setFocusedIndex(0);
      }
      return;
    }

    const keyIndex = itemKeys.indexOf(currentKey);
    if (keyIndex === -1) {
      focusedKeyRef.current = itemKeys[0];
      if (focusedIndex !== 0) {
        setFocusedIndex(0);
      }
      return;
    }

    if (keyIndex !== focusedIndex) {
      setFocusedIndex(keyIndex);
    }
  }, [focusedIndex, isOpen, itemKeys, useItemCountForNavigation]);

  // 포커스 인덱스와 itemCount가 변경될 때 스크롤 및 속성 업데이트
  useEffect(() => {
    if (focusedIndex < 0) {
      updateFocusedAttribute(-1);
      return;
    }

    if (itemCount && itemCount === 0) {
      return;
    }

    const maxIndex =
      typeof itemCount === 'number' ? itemCount : itemKeys ? itemKeys.length : undefined;
    if (maxIndex != null && focusedIndex >= maxIndex) {
      return;
    }

    if (useItemCountForNavigation && itemKeys) {
      if (focusedIndex >= itemKeys.length) {
        return;
      }
      if (!focusedKeyRef.current) {
        focusedKeyRef.current = itemKeys[focusedIndex] ?? null;
      }
    }

    if (useItemCountForNavigation && onNavigateToIndex) {
      const targetIndex = focusedIndex;
      let cancelled = false;
      // 가상화 목록에서 큰 인덱스 점프 시 렌더가 지연되어 첫 프레임에 요소를 못 찾는 이슈가 있어 재시도한다.
      const attemptFocus = (tries: number) => {
        requestAnimationFrame(() => {
          if (cancelled) return;

          const focusableItems = getFocusableItems();
          const targetItem =
            focusableItems.find((item, i) => {
              const dataIndex = item.dataset.index;
              const resolvedIndex = useItemCountForNavigation
                ? dataIndex
                  ? Number(dataIndex)
                  : i
                : i;
              return resolvedIndex === targetIndex;
            }) ?? null;

          if (!targetItem) {
            if (tries > 0) {
              attemptFocus(tries - 1);
              return;
            }
            updateFocusedAttribute(-1);
            return;
          }

          updateFocusedAttribute(targetIndex);
          if (!disableItemFocus) {
            targetItem.focus({ preventScroll: true });
          }
          targetItem.scrollIntoView({ block: 'nearest', behavior: 'instant' });
        });
      };

      onNavigateToIndex(targetIndex);
      attemptFocus(2);
      return () => {
        cancelled = true;
      };
      return;
    }

    const focusableItems = getFocusableItems();
    const targetItem =
      focusableItems.find((item, i) => {
        const dataIndex = item.dataset.index;
        const resolvedIndex = useItemCountForNavigation ? (dataIndex ? Number(dataIndex) : i) : i;
        return resolvedIndex === focusedIndex;
      }) ?? null;

    if (!targetItem) {
      return;
    }

    updateFocusedAttribute(focusedIndex);
    if (!disableItemFocus) {
      targetItem.focus({ preventScroll: true });
    }
    targetItem.scrollIntoView({ block: 'nearest', behavior: 'instant' });
  }, [
    focusedIndex,
    getFocusableItems,
    updateFocusedAttribute,
    disableItemFocus,
    itemCount,
    useItemCountForNavigation,
    onNavigateToIndex,
    itemKeys,
  ]);

  // 키보드 이벤트 핸들러
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const focusableItems = getFocusableItems();
      const focusableCount = useItemCountForNavigation
        ? (itemCount ?? focusableItems.length)
        : focusableItems.length;
      if (focusableCount === 0 || e.nativeEvent.isComposing) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          e.stopPropagation();
          setFocusedIndex((prev) => {
            const next = (prev + 1) % focusableCount;
            if (useItemCountForNavigation && itemKeys && next < itemKeys.length) {
              focusedKeyRef.current = itemKeys[next];
            }
            return next;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          e.stopPropagation();
          setFocusedIndex((prev) => {
            const next = (prev - 1 + focusableCount) % focusableCount;
            if (useItemCountForNavigation && itemKeys && next < itemKeys.length) {
              focusedKeyRef.current = itemKeys[next];
            }
            return next;
          });
          break;
        case 'ArrowLeft':
          if (onSubMenuClose) {
            e.preventDefault();
            onSubMenuClose();
          }
          break;
        case 'Enter': {
          // 포커스된 아이템 클릭
          const targetItem =
            focusableItems.find((item, i) => {
              const dataIndex = item.dataset.index;
              const resolvedIndex = useItemCountForNavigation
                ? dataIndex
                  ? Number(dataIndex)
                  : i
                : i;
              return resolvedIndex === focusedIndex;
            }) ?? null;

          if (targetItem) {
            e.preventDefault();
            targetItem.click();
          }
          break;
        }
      }
    },
    [
      getFocusableItems,
      focusedIndex,
      onSubMenuClose,
      itemCount,
      itemKeys,
      useItemCountForNavigation,
    ],
  );

  // 마우스 오버 시 포커스 인덱스 업데이트
  const handleMouseOver = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      const menuItem = target.closest(
        '[role="menuitem"]:not([aria-disabled="true"])',
      ) as HTMLElement;

      if (menuItem) {
        const focusableItems = getFocusableItems();
        const dataIndex = menuItem.dataset.index;
        const index =
          useItemCountForNavigation && dataIndex
            ? Number(dataIndex)
            : focusableItems.indexOf(menuItem);
        if (index !== -1 && index !== focusedIndex) {
          if (useItemCountForNavigation && itemKeys && index < itemKeys.length) {
            focusedKeyRef.current = itemKeys[index];
          }
          setFocusedIndex(index);
        }
      }
    },
    [getFocusableItems, focusedIndex, itemKeys, useItemCountForNavigation],
  );

  return {
    menuRef,
    handleKeyDown,
    handleMouseOver,
  };
};
